import React, { forwardRef, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PatientPicker from '@components/Patient/PatientPicker';
import { useSubjectLanguage } from '@features/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormHelperText, InputAdornment, TextField, Typography } from '@mui/material';
import { capitalize } from '@utils/utils';
import { MuiTelInput } from 'mui-tel-input';

import type { Patient } from '../../../types/types';
import { type PaymentRequestFormData, paymentRequestSchema } from '../utils/validation';

import { PaymentInfoPopover } from './PaymentInfoText';

export interface PaymentRequestFormProps {
  // Initial data for editing
  defaultValues?: Partial<PaymentRequestFormData>;

  // Pre-filled/locked fields
  patientId?: string;
  patientName?: string;
  patientPhone?: string;
  encounterId?: string;
  appointmentId?: string;
  actionId?: string;

  // Control which fields are shown
  showPatientPicker?: boolean;

  // Form submission handler
  onSubmit: (data: PaymentRequestFormData) => void | Promise<void>;

  // Loading state from parent
  isLoading?: boolean;

  // Form ref for external access
  formRef?: React.RefObject<ReturnType<typeof useForm<PaymentRequestFormData>>>;
}

export const PaymentRequestForm = forwardRef<HTMLFormElement, PaymentRequestFormProps>(
  (
    {
      defaultValues,
      patientId,
      patientName: _patientName,
      patientPhone,
      encounterId,
      appointmentId,
      actionId,
      showPatientPicker = false,
      onSubmit,
      isLoading,
      formRef,
    },
    ref,
  ) => {
    const subjectLanguage = useSubjectLanguage();
    const SubjectLanguage = capitalize(subjectLanguage);
    // Track the selected patient from PatientPicker to check for phone number
    const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>();

    const form = useForm<PaymentRequestFormData>({
      resolver: zodResolver(paymentRequestSchema),
      defaultValues: {
        amount: '',
        patientId: patientId || '',
        reason: '',
        dueDate: '',
        phoneNumber: '',
        appointmentId: appointmentId || '',
        encounterId: encounterId || '',
        actionId: actionId || '',
        ...defaultValues,
      },
    });

    // Watch the patientId field to determine when to show phone field
    const watchedPatientId = form.watch('patientId');

    // Expose form instance to parent if ref provided
    useEffect(() => {
      if (formRef && 'current' in formRef) {
        // @ts-expect-error - We're storing the form instance on the ref
        formRef.current = form;
      }
    }, [form, formRef]);

    // Update patientId when prop changes
    useEffect(() => {
      if (patientId) {
        form.setValue('patientId', patientId);
      }
    }, [patientId, form]);

    return (
      <form ref={ref} onSubmit={form.handleSubmit(onSubmit)} id="payment-request-form">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showPatientPicker && !patientId && (
            <Box>
              <Controller
                name="patientId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <PatientPicker
                      onPatientSelect={(patient: Patient | undefined) => {
                        field.onChange(patient?.patient_id || '');
                        setSelectedPatient(patient); // Track the selected patient to check for phone
                      }}
                      initialPatient={undefined}
                      required
                    />
                    {fieldState.error && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}

          <Controller
            name="amount"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Amount"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Enter the payment amount'}
                required
                fullWidth
                size="small"
                disabled={isLoading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
                onBlur={e => {
                  field.onBlur();
                  // Format to 2 decimal places
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    field.onChange(value.toFixed(2));
                  }
                }}
              />
            )}
          />

          <Controller
            name="reason"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Reason (Optional)"
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Describe what this payment is for'}
                fullWidth
                size="small"
                multiline
                rows={3}
                disabled={isLoading}
                inputProps={{
                  maxLength: 500,
                }}
              />
            )}
          />

          <Controller
            name="dueDate"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Due Date (Optional)"
                type="date"
                value={field.value || ''}
                onChange={e => {
                  const value = e.target.value;
                  field.onChange(value);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'When should this payment be made?'}
                fullWidth
                size="small"
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  },
                }}
              />
            )}
          />

          {/* Patient contact info fields - show when patient selected but no phone available */}
          {(patientId || watchedPatientId) &&
            !patientPhone &&
            !selectedPatient?.mobile_phone &&
            !selectedPatient?.phone && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  {SubjectLanguage} Contact Information
                </Typography>
                <Controller
                  name="phoneNumber"
                  control={form.control}
                  rules={{ required: 'Phone number is required' }}
                  render={({ field, fieldState }) => (
                    <MuiTelInput
                      {...field}
                      label="Phone Number"
                      defaultCountry="US"
                      forceCallingCode
                      onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
                      fullWidth
                      size="small"
                      required
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message || 'Required to send payment request via SMS'
                      }
                      disabled={isLoading}
                    />
                  )}
                />
              </>
            )}

          <PaymentInfoPopover />
        </Box>
      </form>
    );
  },
);

PaymentRequestForm.displayName = 'PaymentRequestForm';

export default PaymentRequestForm;
